import { useState, useRef, useEffect } from "react";
import api from "../../services/api";

const MAX_SIZE = 5 * 1024 * 1024; // 5MB

function KycUploadBox({ label, desc, accept = "image/*,application/pdf", onFile }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sizeError, setSizeError] = useState(null);
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (accept === "application/pdf" && f.type !== "application/pdf" && !f.type.startsWith("image/")) {
      setSizeError("Only PDF, JPG or PNG files are allowed.");
      return;
    }
    if (f.size > MAX_SIZE) {
      setSizeError(`File too large. Max allowed size is 5MB. Your file: ${(f.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }
    setSizeError(null);
    setFile(f);
    onFile && onFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); };

  return (
    <div>
      {label?.trim() && <div className="font-semibold text-[13px] mb-1.5 text-[#191C1D]">{label}</div>}
      <div
        onClick={() => inputRef.current.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-xl bg-[#faf8ff] p-4 min-w-[120px] min-h-[90px] cursor-pointer flex flex-col items-center justify-center gap-2 ${
          sizeError ? "border-red-400 bg-red-50" : "border-[#d1c4e9]"
        }`}
      >
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
        {preview ? (
          <img src={preview} alt="preview" className="w-20 h-[60px] object-cover rounded-md border border-[#d1c4e9]" />
        ) : file ? (
          <div className="text-xs text-[#7b3fbf] font-semibold text-center break-all">📄 {file.name}</div>
        ) : (
          <>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="4" stroke={sizeError ? "#ef4444" : "#9b59d4"} strokeWidth="2" />
              <path d="M8 12h8M12 8v8" stroke={sizeError ? "#ef4444" : "#9b59d4"} strokeWidth="2" strokeLinecap="round" />
            </svg>
            <div className="text-xs text-gray-400 text-center">{desc}</div>
          </>
        )}
        {file && !sizeError && <div className="text-[11px] text-green-600 font-semibold">✓ Uploaded</div>}
      </div>
      {sizeError && <p className="mt-1 text-[11px] font-semibold text-red-500">{sizeError}</p>}
    </div>
  );
}

function LivenessCamera({ onFileSaved, onClose, onLivenessVerified }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [camReady, setCamReady]   = useState(false);
  const [captured, setCaptured]   = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [camError, setCamError]   = useState(null);
  const [status, setStatus]       = useState(null); // null | "loading" | "success" | "error"
  const [msg, setMsg]             = useState("");

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCamReady(true);
      }
    } catch { setCamError("Camera access denied. Please allow camera permission."); }
  };

  const stopCamera = () => streamRef.current?.getTracks().forEach(t => t.stop());

  useEffect(() => { startCamera(); return () => stopCamera(); }, []);

  const capture = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      setCapturedFile(file);
      setCaptured(canvas.toDataURL("image/jpeg"));
      stopCamera();
    }, "image/jpeg", 0.92);
  };

  const retake = () => {
    setCaptured(null); setCapturedFile(null);
    setStatus(null); setMsg(""); setCamReady(false);
    startCamera();
  };

  const verify = async () => {
    if (!capturedFile) return;
    setStatus("loading"); setMsg("");
    try {
      const fd = new FormData();
      fd.append("image", capturedFile);
      const res = await api.post("/kyc/verify-liveness", fd);
      if (res.data?.success) {
        setStatus("success");
        setMsg("Liveness verified ✅");
        onFileSaved && onFileSaved("selfie", capturedFile);
        onLivenessVerified && onLivenessVerified();
        setTimeout(() => onClose(), 1000);
      } else {
        setStatus("error");
        setMsg(res.data?.message || "Liveness check failed. Please retake a clearer photo.");
      }
    } catch (err) {
      setStatus("error");
      setMsg(err.response?.data?.message || "Liveness check failed. Please retake a clearer photo.");
    }
  };

  return (
    <div className="w-[320px] flex flex-col items-center">
      <p className="font-bold text-[16px] mb-1 text-[#191C1D]">Live Selfie Capture</p>
      <p className="text-xs text-gray-400 mb-4 text-center">Position your face clearly and tap Capture</p>

      {/* Camera / Preview */}
      <div className="w-[220px] h-[220px] rounded-full overflow-hidden border-4 border-[#d1c4e9] bg-[#f3eeff] flex items-center justify-center mb-4 relative">
        {captured ? (
          <img src={captured} alt="selfie" className="w-full h-full object-cover" />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
        )}
        {!camReady && !captured && !camError && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#f3eeff]/80 rounded-full">
            <p className="text-xs text-[#7b3fbf] font-semibold">Starting camera...</p>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />

      {camError && <p className="text-xs text-red-500 mb-3 text-center">{camError}</p>}
      {msg && (
        <p className={`text-[12px] font-medium mb-3 text-center ${status === "success" ? "text-green-600" : "text-red-500"}`}>
          {msg}
        </p>
      )}

      {/* Buttons */}
      {!captured ? (
        <button
          onClick={capture}
          disabled={!camReady || !!camError}
          className={`w-full py-3 rounded-[12px] text-[14px] font-bold text-white transition-all ${
            !camReady || camError ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-orange-500 to-red-600 cursor-pointer hover:opacity-90"
          }`}
        >
          📸 Capture Photo
        </button>
      ) : (
        <div className="flex gap-3 w-full">
          <button
            onClick={retake}
            disabled={status === "loading" || status === "success"}
            className="flex-1 py-3 rounded-[12px] text-[13px] font-semibold bg-[#f0f0f0] text-[#333] cursor-pointer hover:bg-[#e5e5e5] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Retake
          </button>
          <button
            onClick={verify}
            disabled={status === "loading" || status === "success"}
            className={`flex-[2] py-3 rounded-[12px] text-[13px] font-bold text-white transition-all ${
              status === "success" ? "bg-green-500 cursor-not-allowed"
              : status === "loading" ? "bg-gray-300 cursor-not-allowed"
              : "bg-gradient-to-r from-orange-500 to-red-600 cursor-pointer hover:opacity-90"
            } disabled:opacity-70`}
          >
            {status === "loading" ? "Verifying..." : status === "success" ? "Verified ✅" : "Verify Liveness"}
          </button>
        </div>
      )}
    </div>
  );
}

function KycModal({ modal, onClose, onFileSaved, onLivenessVerified }) {
  const DoneBtn = () => (
    <button onClick={onClose} className="mt-2.5 w-full bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg py-3 text-base font-bold cursor-pointer">
      Done
    </button>
  );

  let content;
  if (modal === "aadhaar") {
    content = (
      <div className="w-[400px]">
        <div className="font-bold text-lg mb-4">Aadhaar Card Details</div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <KycUploadBox label="Front Side" desc="Click or drag to upload the front of your Aadhaar" accept="application/pdf,image/*" onFile={(v) => onFileSaved("aadhaarFront", v)} />
          <KycUploadBox label="Back Side" desc="Click or drag to upload the back of your Aadhaar" accept="application/pdf,image/*" onFile={(v) => onFileSaved("aadhaarBack", v)} />
        </div>
        <DoneBtn />
      </div>
    );
  } else if (modal === "pan") {
    content = (
      <div className="w-[300px]">
        <div className="font-bold text-lg mb-4">PAN Card Details</div>
        <div className="mb-6">
          <KycUploadBox label="Front Side" desc="Click or drag to upload the front of your Pan" accept="application/pdf,image/*" onFile={(v) => onFileSaved("pan", v)} />
        </div>
        <DoneBtn />
      </div>
    );
  } else if (modal === "selfie") {
    content = <LivenessCamera onFileSaved={onFileSaved} onClose={onClose} onLivenessVerified={onLivenessVerified} />;
  } else if (modal === "gst") {
    content = (
      <div className="w-[340px]">
        <div className="font-bold text-lg mb-4">GST Certificate or Udyam Certificate</div>
        <div className="mb-6">
          <KycUploadBox label="" desc="Click or drag to upload the your GST Certificate or Udyam Certificate" accept="application/pdf,image/*" onFile={(v) => onFileSaved("gst", v)} />
        </div>
        <DoneBtn />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#1e0a3c]/35 z-[1000] flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(30,10,60,0.18)] p-8 relative min-w-[260px]">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-300 text-2xl leading-none cursor-pointer bg-transparent border-none">&times;</button>
        {content}
      </div>
    </div>
  );
}

function KycDocBox({ title, desc, onClick, uploaded, type }) {
  const renderIcon = () => {
    switch (type) {
      case "selfie":
        return (
         <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="6" width="18" height="12" rx="3" stroke="#6b7280" stroke-width="1.8"/>
          <circle cx="12" cy="12" r="3" stroke="#6b7280" stroke-width="1.8"/>
          <path d="M9 6l1.5-2h3L15 6" stroke="#6b7280" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        );

      case "aadhaar":
        return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="#6b7280" stroke-width="1.8"/>
          <circle cx="8" cy="11" r="2" stroke="#6b7280" stroke-width="1.5"/>
          <path d="M12 10h5M12 13h5" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        );

      default:
        return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="#6b7280" stroke-width="1.8"/>
        <circle cx="8" cy="11" r="2" stroke="#6b7280" stroke-width="1.5"/>
        <path d="M12 10h5M12 13h5" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
        );
    }
  };

  return (
    <div
      onClick={onClick}
      className="border-[2px] border-dashed border-[#d6cde3] rounded-xl px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#faf8ff] transition"
    >
      {/* LEFT */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#FFFFFF] flex items-center justify-center">
          {renderIcon()}
        </div>

        <div className="flex flex-col">
          <span className="text-[14px] font-semibold text-[#191C1D]">{title}</span>
          <span className="text-xs text-[#4C444E]">{desc}</span>
        </div>
      </div>

      {/* RIGHT */}
      <div>
        {uploaded ? (
          <div className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-full">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 12l4 4 8-8" stroke="#4C444E" strokeWidth="4" strokeLinecap="round"/>
            </svg>
          </div>
        ) : (
          <div className="w-5 h-5 flex items-center justify-center border-[2px] border-[#7D747F] rounded-full">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="5" cy="14" r="2" fill="#6b7280"/>
              <circle cx="12" cy="14" r="2" fill="#6b7280"/>
              <circle cx="19" cy="14" r="2" fill="#6b7280"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

export default function KycSection({ onFilesChange, onLivenessVerified, requestedDocs }) {
  const [modal, setModal] = useState(null);
  const [files, setFiles] = useState({});
  const filesRef = useRef({});

  const handleFileSaved = (key, value) => {
    filesRef.current = { ...filesRef.current, [key]: value };
    setFiles({ ...filesRef.current });
    onFilesChange && onFilesChange({ ...filesRef.current });
  };

  return (
    <div className="p-6 mb-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0 bg-red-100/20">
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path fill="#dc2626" d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6H6z" />
            <path d="M12 16V10m0 0l-3 3m3-3l3 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="font-bold text-xl text-[#191C1D]">KYC Documents</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
  {(!requestedDocs || requestedDocs.includes("selfie_video")) && (
    <KycDocBox 
      title="Owner Selfie"
      desc="Live selfie photo for face match"
      onClick={() => setModal("selfie")}
      uploaded={!!files.selfie}
      type="selfie"
    />
  )}

  {(!requestedDocs || requestedDocs.includes("aadhaar_front") || requestedDocs.includes("aadhaar_back")) && (
    <KycDocBox 
      title="Aadhaar Card"
      desc="Both sides (PDF/JPG)"
      onClick={() => setModal("aadhaar")}
      uploaded={!!(files.aadhaarFront || files.aadhaarBack)}
      type="aadhaar"
    />
  )}

  {(!requestedDocs || requestedDocs.includes("pan_file")) && (
    <KycDocBox 
      title="PAN Card"
      desc="Front side clear scan"
      onClick={() => setModal("pan")}
      uploaded={!!files.pan}
      type="default"
    />
  )}

  {(!requestedDocs || requestedDocs.includes("gst_certificate")) && (
    <KycDocBox 
      title="GST Certificate or Udyam Certificate"
      desc="Either of the two."
      onClick={() => setModal("gst")}
      uploaded={!!files.gst}
      type="default"
    />
  )}
</div>
      {modal && <KycModal modal={modal} onClose={() => setModal(null)} onFileSaved={handleFileSaved} onLivenessVerified={onLivenessVerified} />}
    </div>
  );
}
